import React, { useCallback, useMemo, useState } from 'react';

// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridCellParams, GridColDef, GridRowModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import {
  CharterLoyaltyCabinClass,
  CharterLoyaltyPointMultiplierResult,
  CharterLoyaltyPointMultiplierUpdateItem,
} from '~/components/Flights/Charter/charter.types';

import {
  useGetCharterLoyaltyPointMultipliers,
  useUpdateCharterLoyaltyPointMultipliers,
} from '~/hooks/apis/flights-charter/useCharterLoyaltyPointMultipliers';

import * as TimeService from '~/services/TimeService';

type AirportPairEntry = {
  depAirport: string;
  arrAirport: string;
};

type FormState = {
  departureDateFrom: Date | null;
  departureDateTo: Date | null;
  cabinClass: Array<CharterLoyaltyCabinClass>;
  airportFilterEnabled: boolean;
  airportPairs: Array<AirportPairEntry>;
};

type SearchFilters = {
  departureDateFrom: string;
  departureDateTo: string;
  cabinClass: Array<CharterLoyaltyCabinClass>;
  airportPairs?: Array<AirportPairEntry>;
};

type LoyaltyRow = {
  id: string;
  flightInstanceId: string;
  depDate: string;
  depAirport: string;
  arrAirport: string;
} & Partial<Record<CharterLoyaltyCabinClass, number>>;

const CABIN_CLASS_OPTIONS: Array<{ label: string; value: CharterLoyaltyCabinClass }> = [
  { label: 'Economy', value: 'ECONOMY' },
  { label: 'Premium Economy', value: 'PREMIUM_ECONOMY' },
  { label: 'Lux Row', value: 'LUX_ROW' },
  { label: 'Lux Row (Pax One)', value: 'LUX_ROW_PAX_ONE' },
  { label: 'Business', value: 'BUSINESS' },
];

const allCabinClasses = CABIN_CLASS_OPTIONS.map((option) => option.value);

const AIRPORT_OPTIONS = ['MLE', 'MEL'] as const;

const DEFAULT_DEPARTURE_DATE_FROM = new Date(2026, 4, 18);
const DEFAULT_DEPARTURE_DATE_TO = new Date(2026, 11, 31);

const VALID_MULTIPLIERS = [1, 2, 3] as const;
type LoyaltyMultiplier = (typeof VALID_MULTIPLIERS)[number];

function toLoyaltyMultiplier(value: unknown): LoyaltyMultiplier {
  const num = Number(value);
  if (num === 1 || num === 2 || num === 3) {
    return num;
  }
  throw new RangeError(`Invalid loyalty point multiplier: ${String(value)}`);
}

const formatDisplayDate = (value: string) => TimeService.formatDateSlashes(value) || value;

const mapResultsToRows = (results: Array<CharterLoyaltyPointMultiplierResult>): Array<LoyaltyRow> => {
  return results.map((result) => {
    const multipliersByCabin = result.multipliers.reduce<Partial<Record<CharterLoyaltyCabinClass, number>>>(
      (acc, item) => ({ ...acc, [item.cabinClass]: item.loyaltyPointMultiplier }),
      {},
    );

    return {
      id: result.flightInstanceId,
      flightInstanceId: result.flightInstanceId,
      depDate: result.depDate,
      depAirport: result.depAirport,
      arrAirport: result.arrAirport,
      ...multipliersByCabin,
    } as LoyaltyRow;
  });
};

const buildUpdatePayload = (
  editedRows: Map<string, LoyaltyRow>,
  activeCabinClasses: Array<CharterLoyaltyCabinClass>,
): { items: Array<CharterLoyaltyPointMultiplierUpdateItem> } => ({
  items: Array.from(editedRows.values()).map((row) => ({
    flightInstanceId: row.flightInstanceId,
    multipliers: activeCabinClasses
      .filter((cabinClass) => row[cabinClass] != null)
      .map((cabinClass) => ({
        cabinClass,
        loyaltyPointMultiplier: toLoyaltyMultiplier(row[cabinClass]),
      })),
  })),
});

export default function CharterLoyaltyPointsPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState<FormState>({
    departureDateFrom: DEFAULT_DEPARTURE_DATE_FROM,
    departureDateTo: DEFAULT_DEPARTURE_DATE_TO,
    cabinClass: allCabinClasses,
    airportFilterEnabled: false,
    airportPairs: [{ depAirport: AIRPORT_OPTIONS[0], arrAirport: AIRPORT_OPTIONS[1] }],
  });
  const [searchFilters, setSearchFilters] = useState<SearchFilters | null>(null);
  const [editedRows, setEditedRows] = useState<Map<string, LoyaltyRow>>(new Map());

  const loyaltyQuery = useGetCharterLoyaltyPointMultipliers(searchFilters);
  const updateMutation = useUpdateCharterLoyaltyPointMultipliers();

  const baseRows = useMemo(() => {
    if (!searchFilters || !loyaltyQuery.data) {
      return [];
    }
    return mapResultsToRows(loyaltyQuery.data);
  }, [loyaltyQuery.data, searchFilters]);

  const baseRowMap = useMemo(() => {
    const map = new Map<string, LoyaltyRow>();
    for (const row of baseRows) {
      map.set(row.flightInstanceId, row);
    }
    return map;
  }, [baseRows]);

  const rows = useMemo(() => {
    return baseRows.map((baseRow) => editedRows.get(baseRow.flightInstanceId) ?? baseRow);
  }, [baseRows, editedRows]);

  const handleSearch = useCallback(() => {
    if (!formState.departureDateFrom || !formState.departureDateTo || formState.cabinClass.length === 0) {
      return;
    }

    if (formState.departureDateFrom > formState.departureDateTo) {
      enqueueSnackbar('Departure date from must be before departure date to.', { variant: 'warning' });
      return;
    }

    setEditedRows(new Map());
    setSearchFilters({
      departureDateFrom: TimeService.formatDateISO(formState.departureDateFrom),
      departureDateTo: TimeService.formatDateISO(formState.departureDateTo),
      cabinClass: formState.cabinClass,
      airportPairs: formState.airportFilterEnabled ? formState.airportPairs : undefined,
    });
  }, [enqueueSnackbar, formState]);

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<LoyaltyRow>) => {
      setEditedRows((current) => {
        const original = baseRowMap.get(newRow.flightInstanceId);
        const next = new Map(current);

        const hasChanges =
          !original ||
          (searchFilters?.cabinClass ?? []).some((cabinClass) => newRow[cabinClass] !== original[cabinClass]);

        if (hasChanges) {
          next.set(newRow.flightInstanceId, newRow);
        } else {
          next.delete(newRow.flightInstanceId);
        }

        return next;
      });

      return newRow;
    },
    [baseRowMap, searchFilters],
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<LoyaltyRow>) => {
      const field = params.field as CharterLoyaltyCabinClass;
      if (!searchFilters?.cabinClass.includes(field)) {
        return '';
      }

      const original = baseRowMap.get(params.row.flightInstanceId);
      if (!original) {
        return '';
      }

      return params.value !== original[field] ? 'cell-modified' : '';
    },
    [baseRowMap, searchFilters],
  );

  const handleSave = useCallback(async () => {
    if (!searchFilters || editedRows.size === 0) {
      return;
    }

    try {
      const payload = buildUpdatePayload(editedRows, searchFilters.cabinClass);
      await updateMutation.mutateAsync(payload);
      enqueueSnackbar('Loyalty point multipliers saved successfully.', { variant: 'success' });
      setEditedRows(new Map());
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to save loyalty point multipliers.', {
        variant: 'error',
      });
    }
  }, [editedRows, enqueueSnackbar, searchFilters, updateMutation]);

  const columns = useMemo<Array<GridColDef<LoyaltyRow>>>(() => {
    if (!searchFilters) {
      return [];
    }

    const staticColumns: Array<GridColDef<LoyaltyRow>> = [
      {
        field: 'depDate',
        headerName: 'Departure date',
        width: 130,
        valueFormatter: (value) => formatDisplayDate(String(value)),
      },
      {
        field: 'route',
        headerName: 'Route',
        width: 140,
        valueGetter: (_value, row) => `${row.depAirport} → ${row.arrAirport}`,
      },
    ];

    const multiplierColumns: Array<GridColDef<LoyaltyRow>> = searchFilters.cabinClass.map((cabinClass) => ({
      field: cabinClass,
      headerName: CABIN_CLASS_OPTIONS.find((option) => option.value === cabinClass)?.label ?? cabinClass,
      flex: 1,
      minWidth: 130,
      type: 'singleSelect',
      editable: true,
      valueOptions: [...VALID_MULTIPLIERS],
      align: 'center' as const,
      headerAlign: 'center' as const,
    }));

    return [...staticColumns, ...multiplierColumns];
  }, [searchFilters]);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Charter Loyalty Points
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search charter departures and manage loyalty point multipliers by cabin class.
        </Typography>
      </Stack>

      <Card>
        <CardHeader title="Filters" sx={{ pb: 0 }} />
        <CardContent>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <DatePicker
                  label="Departure date from"
                  value={formState.departureDateFrom ? TimeService.toDayJs(formState.departureDateFrom) : null}
                  onChange={(newValue: Dayjs | null) =>
                    setFormState((current) => ({ ...current, departureDateFrom: newValue ? newValue.toDate() : null }))
                  }
                  maxDate={formState.departureDateTo ? TimeService.toDayJs(formState.departureDateTo) : undefined}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <DatePicker
                  label="Departure date to"
                  value={formState.departureDateTo ? TimeService.toDayJs(formState.departureDateTo) : null}
                  onChange={(newValue: Dayjs | null) =>
                    setFormState((current) => ({ ...current, departureDateTo: newValue ? newValue.toDate() : null }))
                  }
                  minDate={formState.departureDateFrom ? TimeService.toDayJs(formState.departureDateFrom) : undefined}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                    value: formState.cabinClass,
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as Array<CharterLoyaltyCabinClass>).map((value) => {
                          const label = CABIN_CLASS_OPTIONS.find((option) => option.value === value)?.label ?? value;
                          return <Chip key={value} size="small" label={label} />;
                        })}
                      </Box>
                    ),
                  }}
                  fullWidth
                  size="small"
                  label="Cabin class"
                  onChange={(event) => {
                    const value = event.target.value;
                    setFormState((current) => ({
                      ...current,
                      cabinClass:
                        typeof value === 'string'
                          ? (value.split(',') as Array<CharterLoyaltyCabinClass>)
                          : (value as Array<CharterLoyaltyCabinClass>),
                    }));
                  }}
                >
                  {CABIN_CLASS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.airportFilterEnabled}
                    onChange={(event) =>
                      setFormState((current) => ({ ...current, airportFilterEnabled: event.target.checked }))
                    }
                  />
                }
                label="Filter by airports"
              />

              {formState.airportFilterEnabled &&
                formState.airportPairs.map((pair, index) => (
                  <Grid container spacing={2} key={index} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Departure airport"
                        value={pair.depAirport}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            airportPairs: current.airportPairs.map((p, i) =>
                              i === index ? { ...p, depAirport: event.target.value } : p,
                            ),
                          }))
                        }
                      >
                        {AIRPORT_OPTIONS.map((code) => (
                          <MenuItem key={code} value={code}>
                            {code}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Arrival airport"
                        value={pair.arrAirport}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            airportPairs: current.airportPairs.map((p, i) =>
                              i === index ? { ...p, arrAirport: event.target.value } : p,
                            ),
                          }))
                        }
                      >
                        {AIRPORT_OPTIONS.map((code) => (
                          <MenuItem key={code} value={code}>
                            {code}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid size="auto">
                      <Button
                        variant="text"
                        color="primary"
                        size="small"
                        disabled={formState.airportPairs.length <= 1}
                        onClick={() =>
                          setFormState((current) => ({
                            ...current,
                            airportPairs: current.airportPairs.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                ))}

              {formState.airportFilterEnabled && (
                <Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() =>
                      setFormState((current) => ({
                        ...current,
                        airportPairs: [
                          ...current.airportPairs,
                          { depAirport: AIRPORT_OPTIONS[0], arrAirport: AIRPORT_OPTIONS[1] },
                        ],
                      }))
                    }
                  >
                    Add airport pair
                  </Button>
                </Box>
              )}
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={
                  loyaltyQuery.isFetching ||
                  !formState.departureDateFrom ||
                  !formState.departureDateTo ||
                  !formState.cabinClass.length
                }
              >
                Search
              </Button>
              <LoadingButton
                variant="contained"
                loading={updateMutation.isPending}
                onClick={handleSave}
                disabled={!editedRows.size || !searchFilters}
              >
                Save
              </LoadingButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loyaltyQuery.error ? <Alert severity="error">{loyaltyQuery.error.message}</Alert> : null}
      {searchFilters && !loyaltyQuery.isFetching && rows.length === 0 ? (
        <Alert severity="info">No loyalty point multiplier results found for selected filters.</Alert>
      ) : null}

      {searchFilters ? (
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loyaltyQuery.isFetching}
            hideFooter
            disableRowSelectionOnClick
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) =>
              enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update cell.', { variant: 'error' })
            }
            getCellClassName={getCellClassName}
            sx={{ '& .cell-modified': { bgcolor: 'warning.light' } }}
          />
        </Box>
      ) : null}
    </Stack>
  );
}
